// Admin session: token storage plus a single-flight refresh.
//
// Storage choice — access token in memory, refresh token in sessionStorage:
//   - httpOnly cookies would be stronger, but they require CSRF protection that
//     does not exist anywhere in this stack yet; a half-migrated cookie setup is
//     worse than a clean Bearer one.
//   - sessionStorage over localStorage bounds the blast radius of an XSS to the
//     tab's lifetime.
// Combined with a 15-minute access token, an 8-hour refresh token and
// server-side revocation, a stolen token has a short and revocable life.
//
// Consequence: never render server-supplied values as HTML in this app.

const REFRESH_STORAGE_KEY = 'bostil.admin.rt';

const API_BASE_URL: string = (import.meta.env.VITE_API_BASE_URL ?? '').replace(/\/$/, '');

export function apiUrl(path: string): string {
  return `${API_BASE_URL}${path}`;
}

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status?: number,
    readonly code?: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/** Thrown when the session is unrecoverable; RequireAuth turns this into a redirect. */
export class SessionExpiredError extends Error {
  constructor(message = 'Sua sessão expirou. Faça login novamente.') {
    super(message);
    this.name = 'SessionExpiredError';
  }
}

export interface AdminProfile {
  id: string;
  username: string;
  displayName: string;
  role: 'super_admin' | 'admin';
  isActive: boolean;
  mustChangePassword: boolean;
  lastLoginAt: string | null;
  createdAt: string;
}

interface TokenPair {
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
}

let accessToken: string | null = null;
let refreshInFlight: Promise<string> | null = null;

export function getRefreshToken(): string | null {
  try {
    return sessionStorage.getItem(REFRESH_STORAGE_KEY);
  } catch {
    return null;
  }
}

function storeTokens(tokens: TokenPair): void {
  accessToken = tokens.accessToken;
  try {
    sessionStorage.setItem(REFRESH_STORAGE_KEY, tokens.refreshToken);
  } catch {
    // Private-mode storage failures are survivable: the session simply won't
    // outlive a reload.
  }
}

export function clearSession(): void {
  accessToken = null;
  try {
    sessionStorage.removeItem(REFRESH_STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

export function hasSession(): boolean {
  return accessToken !== null || getRefreshToken() !== null;
}

async function parseBody(res: Response): Promise<Record<string, unknown>> {
  try {
    return (await res.json()) as Record<string, unknown>;
  } catch {
    return {};
  }
}

function toApiError(body: Record<string, unknown>, status: number, fallback: string): ApiError {
  const message = typeof body.message === 'string' ? body.message : fallback;
  const code = typeof body.code === 'string' ? body.code : undefined;
  return new ApiError(message, status, code);
}

async function doRefresh(): Promise<string> {
  const rt = getRefreshToken();
  if (!rt) throw new SessionExpiredError();

  let res: Response;
  try {
    res = await fetch(apiUrl('/v1/admin/refresh'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ refreshToken: rt }),
    });
  } catch {
    throw new ApiError('Não foi possível alcançar o servidor de autenticação.');
  }

  if (!res.ok) {
    clearSession();
    throw new SessionExpiredError();
  }

  const body = (await res.json()) as { tokens?: TokenPair };
  if (!body.tokens) {
    clearSession();
    throw new SessionExpiredError();
  }
  storeTokens(body.tokens);
  return body.tokens.accessToken;
}

/**
 * Ensures at most one refresh is in flight.
 *
 * Refresh tokens ROTATE and reuse is treated as theft (the server kills the
 * whole token family). Without this guard, a page that fires several requests on
 * mount would trigger concurrent refreshes, and every one after the first would
 * present an already-used token — logging the admin out.
 */
function refreshOnce(): Promise<string> {
  if (!refreshInFlight) {
    refreshInFlight = doRefresh().finally(() => {
      refreshInFlight = null;
    });
  }
  return refreshInFlight;
}

async function authedFetch(path: string, init: RequestInit = {}, retry = true): Promise<Response> {
  // A reload loses the in-memory access token but keeps the refresh token, so
  // recover the session before the first call rather than burning a 401.
  if (!accessToken && getRefreshToken()) {
    await refreshOnce();
  }

  const headers = new Headers(init.headers);
  headers.set('Accept', 'application/json');
  if (init.body !== undefined && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }
  if (accessToken) headers.set('Authorization', `Bearer ${accessToken}`);

  let res: Response;
  try {
    res = await fetch(apiUrl(path), { ...init, headers });
  } catch {
    throw new ApiError('Não foi possível alcançar o servidor de autenticação.');
  }

  if (res.status === 401 && retry) {
    await refreshOnce();
    return authedFetch(path, init, false);
  }

  return res;
}

/** Typed request helper. Preserves the server's machine-readable `code`. */
export async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await authedFetch(path, init);
  const body = await parseBody(res);
  if (!res.ok) {
    throw toApiError(body, res.status, 'A requisição falhou.');
  }
  return body as T;
}

export function get<T>(path: string): Promise<T> {
  return request<T>(path, { method: 'GET' });
}
export function post<T>(path: string, data?: unknown): Promise<T> {
  return request<T>(path, {
    method: 'POST',
    ...(data !== undefined ? { body: JSON.stringify(data) } : {}),
  });
}
export function patch<T>(path: string, data?: unknown): Promise<T> {
  return request<T>(path, {
    method: 'PATCH',
    ...(data !== undefined ? { body: JSON.stringify(data) } : {}),
  });
}
export function put<T>(path: string, data?: unknown): Promise<T> {
  return request<T>(path, {
    method: 'PUT',
    ...(data !== undefined ? { body: JSON.stringify(data) } : {}),
  });
}
export function del<T>(path: string, data?: unknown): Promise<T> {
  return request<T>(path, {
    method: 'DELETE',
    ...(data !== undefined ? { body: JSON.stringify(data) } : {}),
  });
}

export async function login(
  username: string,
  password: string,
): Promise<{ admin: AdminProfile; tokens: TokenPair }> {
  let res: Response;
  try {
    res = await fetch(apiUrl('/v1/admin/login'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ username, password }),
    });
  } catch {
    throw new ApiError('Não foi possível alcançar o servidor de autenticação.');
  }

  const body = await parseBody(res);
  if (!res.ok) {
    throw toApiError(body, res.status, 'Falha ao entrar.');
  }

  const parsed = body as unknown as { admin: AdminProfile; tokens: TokenPair };
  storeTokens(parsed.tokens);
  return parsed;
}

export async function logout(): Promise<void> {
  const refreshToken = getRefreshToken();
  try {
    await authedFetch('/v1/admin/logout', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken }),
    });
  } catch {
    // Logging out locally matters more than the server round-trip succeeding.
  }
  clearSession();
}

export async function fetchMe(): Promise<AdminProfile> {
  const body = await get<{ admin: AdminProfile }>('/v1/admin/me');
  return body.admin;
}

export async function changePassword(
  currentPassword: string,
  newPassword: string,
): Promise<AdminProfile> {
  const res = await authedFetch('/v1/admin/change-password', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ currentPassword, newPassword }),
  });
  const body = await parseBody(res);
  if (!res.ok) throw toApiError(body, res.status, 'Não foi possível trocar a senha.');

  // The server revokes every session and issues a fresh pair in the same
  // response, so adopt it instead of bouncing the user back to the login page.
  const parsed = body as unknown as { admin: AdminProfile; tokens: TokenPair };
  if (parsed.tokens) storeTokens(parsed.tokens);
  return parsed.admin;
}
